// Wire-format types and API contract for Notice Management (PRD 13).
// Mirrors the shape used by `termsApi` / `managementGuidelinesApi` so the
// adapter/error patterns stay consistent across features.
// See docs/api-contracts/notice-management.md.

import type { PortalRole } from '@/app/store/auth.store';

// PRD 13 §4.3 — wire format is UPPERCASE English; UI maps to Korean/English
// labels. Audience drives channel exposure (patient app / staff web / both).
export type NoticeAudience = 'PATIENT' | 'STAFF' | 'BOTH';
export type NoticeCategory = 'GENERAL' | 'SYSTEM' | 'UPDATE' | 'CONTENT';
// HIDDEN is a system-internal state (a notice published then taken down). It is
// never offered in the form's status <select> — only PUBLISHED / DRAFT are.
export type NoticeStatus = 'PUBLISHED' | 'DRAFT' | 'HIDDEN';

export const NOTICE_AUDIENCES: readonly NoticeAudience[] = [
  'PATIENT',
  'STAFF',
  'BOTH',
] as const;

export const NOTICE_CATEGORIES: readonly NoticeCategory[] = [
  'GENERAL',
  'SYSTEM',
  'UPDATE',
  'CONTENT',
] as const;

// Status options selectable in the create/edit form (PRD 13 §4.2 — HIDDEN
// excluded). PUBLISHED is the default per §3.2.
export const NOTICE_FORM_STATUSES: readonly Extract<
  NoticeStatus,
  'PUBLISHED' | 'DRAFT'
>[] = ['PUBLISHED', 'DRAFT'] as const;

// Snapshot of the admin who first created the notice. PRD 13 §3.1 — preserved
// on edit (never overwritten by the editor). Subsequent renames / role changes
// on that admin do NOT mutate this snapshot.
export interface NoticeAuthor {
  name: string;
  email: string;
  // English role key, used to render the Role Badge in the author cell.
  authorGrade: PortalRole;
}

// List row shape (PRD 13 §4.1). The heavy HTML `content` field is omitted from
// list responses — fetched per-notice via getNotice for the edit form.
export interface NoticeSummary {
  // ad_notice.id is BIGINT UNSIGNED → number on the wire.
  id: number;
  // Server-minted display code (PRD §4.3, e.g. `N-001`; DisplayCodeAllocator).
  // Optional for forward-compat: when absent (mock / legacy) the table falls
  // back to deriving `N-00{id}` from the numeric id.
  nid?: string;
  audience: NoticeAudience;
  title: string;
  category: NoticeCategory;
  status: NoticeStatus;
  author: NoticeAuthor;
  // Patient/staff app-read count (PRD 13 §3.1). Admin views do NOT increment
  // it. Server-owned — the FE never writes this.
  views: number;
  // ISO 8601 UTC datetime. Server-assigned on create. The list default sort is
  // `createdAt` DESC (PRD 13 §3.1); rendered as `YYYY.MM.DD HH:MM`.
  createdAt: string;
  // True when the notice published a patient-app push of its title. Forced
  // false when `audience === 'STAFF'` (PRD 13 §4.3).
  pushOnPublish: boolean;
}

// Detail view adds the HTML body (heavy field — omitted from list responses).
export interface Notice extends NoticeSummary {
  // HTML body authored via the contenteditable RTE. Stored/returned verbatim.
  content: string;
}

// PRD 13 §3.2 — `status` carries the publish intent (PUBLISHED vs DRAFT); there
// is no separate publish endpoint. `pushOnPublish` is coerced to false by the
// server when `audience === 'STAFF'`. `author` is server-derived from the
// session (not part of the request body).
export interface CreateNoticeInput {
  audience: NoticeAudience;
  title: string;
  category: NoticeCategory;
  status: Extract<NoticeStatus, 'PUBLISHED' | 'DRAFT'>;
  content: string;
  pushOnPublish: boolean;
}

export type UpdateNoticeInput = Partial<CreateNoticeInput>;

export type NoticeApiErrorCode =
  | 'AUDIENCE_REQUIRED'
  | 'TITLE_REQUIRED'
  | 'TITLE_LENGTH_INVALID'
  | 'CATEGORY_REQUIRED'
  | 'STATUS_REQUIRED'
  | 'CONTENT_REQUIRED'
  | 'CONTENT_TOO_LONG'
  | 'NOT_FOUND'
  | 'INSUFFICIENT_PERMISSION'
  // SHCT-669 — the request never reached the server (offline / DNS / CORS).
  // Kept distinct from UNKNOWN so the user is told to check their connection
  // instead of seeing a generic failure (admin-web error standard §7.1 / §5).
  | 'NETWORK'
  | 'UNKNOWN';

export class NoticeApiError extends Error {
  readonly code: NoticeApiErrorCode;
  readonly fields?: Record<string, string>;
  readonly meta?: Record<string, unknown>;

  constructor(
    code: NoticeApiErrorCode,
    message: string,
    options?: { fields?: Record<string, string>; meta?: Record<string, unknown> },
  ) {
    super(message);
    this.name = 'NoticeApiError';
    this.code = code;
    this.fields = options?.fields;
    this.meta = options?.meta;
    Object.setPrototypeOf(this, NoticeApiError.prototype);
  }
}

export interface NoticeApi {
  // PRD 13 §3.1 — full list, unpaginated. Search/sort/pagination run
  // client-side (prototype parity) until BE confirms server-side support.
  // Ordered by `createdAt` DESC.
  list(): Promise<NoticeSummary[]>;
  // PRD 13 §3.3 — detail view (includes the HTML body) for the edit form.
  get(id: number): Promise<Notice>;
  // PRD 13 §3.2 — create with status = PUBLISHED or DRAFT. Author is derived
  // server-side from the session.
  create(input: CreateNoticeInput): Promise<Notice>;
  // PRD 13 §3.3 — edit. Author snapshot is preserved (never overwritten).
  update(id: number, input: UpdateNoticeInput): Promise<Notice>;
  // PRD 13 §3.4 — permanent delete. Root-only (no PUBLICATION:DELETE level in
  // the catalog → gated by the auth-store `isRoot` flag; BE enforces).
  remove(id: number): Promise<void>;
}
